// source-dialog.jsx
import React, { useState, useEffect, useRef, useCallback } from "react"
import { Editor } from "@monaco-editor/react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import { ExclamationCircleIcon } from "@heroicons/react/24/outline"

const ButtonSourceDialog = ({
  buttonElement = null,
  onClose,
}) => {
  const [content, setContent] = useState("")
  const [originalContent, setOriginalContent] = useState("")
  const [hasChanges, setHasChanges] = useState(false)
  const editorRef = useRef(null)

  // Function to format HTML
  const formatHtml = (html) => {
    if (!html) return ""

    try {
      // Simple HTML formatting - add indentation and line breaks
      let formatted = html
        .replace(/></g, '>\n<')
        .replace(/^\s+|\s+$/g, '')

      // Add proper indentation
      const lines = formatted.split('\n')
      let indentLevel = 0
      const indentSize = 2

      const formattedLines = lines.map(line => {
        const trimmed = line.trim()
        if (!trimmed) return ''

        // Decrease indent for closing tags
        if (trimmed.startsWith('</')) {
          indentLevel = Math.max(0, indentLevel - 1)
        }

        const indentedLine = ' '.repeat(indentLevel * indentSize) + trimmed

        // Increase indent for opening tags (but not self-closing)
        if (trimmed.startsWith('<') &&
            !trimmed.startsWith('</') &&
            !trimmed.endsWith('/>') &&
            !['br', 'hr', 'img', 'input', 'meta', 'link'].some(tag =>
              trimmed.startsWith(`<${tag}`)
            )) {
          indentLevel++
        }

        return indentedLine
      })

      return formattedLines.join('\n')
    } catch (error) {
      console.warn('Failed to format HTML:', error)
      return html
    }
  }

  // Load current HTML when dialog opens
  useEffect(() => {
    if (buttonElement) {
      const formatted = formatHtml(buttonElement.outerHTML)
      setContent(formatted)
      setOriginalContent(formatted)
      setHasChanges(false)
    }
  }, [buttonElement])

  // Track changes in content
  useEffect(() => {
    setHasChanges(content !== originalContent)
  }, [content, originalContent])

  const handleUpdate = () => {
    if (buttonElement && content.trim() !== "") {
      try {
        // Create a temporary container to parse the HTML
        const tempDiv = document.createElement("div")
        tempDiv.innerHTML = content.trim()

        // Get the first element from the parsed HTML
        const newElement = tempDiv.firstElementChild

        if (newElement) {
          // Replace the current button with the new element
          buttonElement.parentNode.replaceChild(newElement, buttonElement)
          onClose({ applied: true })
        } else {
          alert("Invalid HTML: No element found in the source code")
        }
      } catch (error) {
        alert("Error parsing HTML: " + error.message)
      }
    } else {
      onClose(null)
    }
  }

  const handleFormatHtml = useCallback(() => {
    if (editorRef.current) {
      const editor = editorRef.current
      const formatAction = editor.getAction('editor.action.formatDocument')
      if (formatAction) {
        formatAction.run()
      }
    }
  }, [])

  const handleEditorDidMount = (editor) => {
    editorRef.current = editor
    // Trigger formatting on mount and update original content after formatting
    setTimeout(() => {
      const formatAction = editor.getAction('editor.action.formatDocument')
      if (formatAction) {
        formatAction.run()
        // Update original content after formatting to prevent false change detection
        setTimeout(() => {
          const formattedContent = editor.getValue()
          setOriginalContent(formattedContent)
          setHasChanges(false)
        }, 300)
      } else {
        // If no format action available, just set original content
        setTimeout(() => {
          const currentContent = editor.getValue()
          setOriginalContent(currentContent)
          setHasChanges(false)
        }, 100)
      }
    }, 200)
  }


  return (
    <Modal
      isOpen={true}
      onClose={() => onClose(null)}
      title="Edit Button Source Code"
      variant="admin"
      closeOnEscape={true}
      className="w-full max-w-4xl max-h-[80vh]"
      footerContent={(
        <div className="flex items-center justify-between">
          {hasChanges ? (
            <div className="flex items-center gap-2">
              <ExclamationCircleIcon className="w-4 h-4 text-amber-500" />
              <p className="text-sm text-admin_text_secondary">Pending Changes</p>
            </div>
          ) : (
            <div></div>
          )}
          <Button
            variant="admin_primary"
            onClick={handleUpdate}
            type="button"
          >
            Update
          </Button>
        </div>
      )}
    >
      <div className="overflow-hidden h-96">
        <Editor
          width="100%"
          height="100%"
          language="html"
          value={content}
          onChange={(value) => setContent(value || "")}
          theme="vs-dark"
          onMount={handleEditorDidMount}
          options={{
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            lineNumbers: "on",
            wordWrap: "on",
            automaticLayout: true,
            fontSize: 14,
            fontFamily: 'Monaco, "Lucida Console", "Courier New", monospace',
            formatOnPaste: true,
            formatOnType: true,
            tabSize: 2,
            insertSpaces: true,
            detectIndentation: false,
            trimAutoWhitespace: true,
            autoIndent: "full",
          }}
        />
      </div>
    </Modal>
  )
}

export default ButtonSourceDialog